/**
 * One live log stream from the hub (a job, a session or a pipeline item) while `enabled`.
 * Opens it with POST /api/logs/<type>:<id>, receives chunks into the store through SSE,
 * and always closes it on the hub when the view goes away, so the machine stops tailing.
 */
import { useCallback, useEffect, useState } from 'react';
import type { LogRef } from '@orgus/protocol';
import { closeLog, openLog } from './api.ts';
import { useOrgus, useLog } from './store.ts';
import type { LogBuffer } from './store.ts';

export interface LogStream {
  streamId: string | null;
  log: LogBuffer | undefined;
  loading: boolean;
  error: unknown;
  retry: () => void;
}

/** Forget a stream locally and close it on the hub. */
function release(streamId: string): void {
  useOrgus.getState().clearLog(streamId);
  void closeLog(streamId);
}

export function useLogStream(ref: LogRef | null, enabled: boolean): LogStream {
  const mode = useOrgus((s) => s.mode);
  const [streamId, setStreamId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const log = useLog(streamId);
  const type = ref?.type;
  const id = ref?.id;

  useEffect(() => {
    if (!enabled || !type || !id || mode !== 'live') return;
    let cancelled = false;
    let opened: string | null = null;
    setError(null);
    setStreamId(null);
    setLoading(true);
    openLog({ type, id })
      .then((r) => {
        // The view went away while the request was in flight: close what it opened.
        if (cancelled) return release(r.streamId);
        opened = r.streamId;
        useOrgus.getState().trackLog(r.streamId);
        setStreamId(r.streamId);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (opened) release(opened);
      setStreamId(null);
    };
  }, [enabled, type, id, mode, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { streamId, log, loading, error, retry };
}
